'use client'

import { create } from 'zustand'
import { authService, AuthSession, AuthCredentials, RedmineUser } from '../../services/auth/auth-service'
import { handleAuthError } from '@/lib/api-error-handler'

interface AuthStore {
    user: RedmineUser | null
    session: AuthSession | null
    isLoading: boolean
    isInitialized: boolean
    error: string | null
    login: (credentials: AuthCredentials) => Promise<void>
    loginWithApiKey: (apiKey: string) => Promise<void>
    logout: () => void
    initialize: () => Promise<void>
    refreshSession: () => Promise<void>
    extendSession: () => void
    isAuthenticated: () => boolean
    clearError: () => void
    initializeAuth: () => Promise<void>
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
    user: null,
    session: null,
    isLoading: false, // Start as false, will be set to true during initialization if needed
    isInitialized: false,
    error: null,

    login: async (credentials: AuthCredentials) => {
        console.log('[AuthStore] Starting login process...')
        set({ isLoading: true, error: null })
        try {
            const session = await authService.login(credentials)
            console.log('[AuthStore] Login successful, setting session')

            // Store JWT token in cookie for API calls
            if (session.jwtToken) {
                document.cookie = `jwt-token=${session.jwtToken}; path=/; max-age=${24 * 60 * 60}; samesite=lax`
            }

            set({
                session,
                user: session.user,
                isLoading: false,
                error: null
            })
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Login failed'
            console.error('[AuthStore] Login failed with error:', errorMessage)
            set({
                isLoading: false,
                error: errorMessage,
                session: null,
                user: null
            })
            throw error
        }
    },

    loginWithApiKey: async (apiKey: string) => {
        set({ isLoading: true, error: null })
        try {
            const session = await authService.loginWithApiKey(apiKey)

            // Store JWT token in cookie for API calls
            if (session.jwtToken) {
                document.cookie = `jwt-token=${session.jwtToken}; path=/; max-age=${24 * 60 * 60}; samesite=lax`
            }

            set({
                session,
                user: session.user,
                isLoading: false,
                error: null
            })
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'API key authentication failed'
            set({
                isLoading: false,
                error: errorMessage,
                session: null,
                user: null
            })
            throw error
        }
    },

    logout: () => {
        authService.logout()
        // Clear JWT token cookie
        document.cookie = 'jwt-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
        set({
            session: null,
            user: null,
            error: null,
            isLoading: false
        })
    },

    refreshSession: async () => {
        try {
            const session = await authService.refreshSession()
            if (session) {
                set({
                    user: session.user,
                    session: session
                })
            }
        } catch (error) {
            set({
                user: null,
                session: null,
                isLoading: false
            })
        }
    },
    extendSession: () => {
        const success = authService.extendSession()
        if (success) {
            const session = authService.getSession()
            if (session) {
                set({ session })
            }
        }
    },

    clearError: () => {
        set({ error: null })
    },

    initializeAuth: async () => {
        const currentState = get()

        console.log('[AuthStore] initializeAuth called - isInitialized:', currentState.isInitialized)

        // If we already have a valid session and are initialized, don't do anything
        if (currentState.isInitialized) {
            console.log('[AuthStore] Already initialized, skipping')
            if (currentState.session?.user) {
                return
            } else {
                return
            }
        }

        // If we have a session from rehydration, validate and use it
        if (currentState.session?.user) {

            // Validate session expiry
            if (currentState.session.expiresAt > Date.now()) {
                set({
                    session: currentState.session,
                    user: currentState.session.user,
                    isLoading: false,
                    isInitialized: true,
                    error: null
                })

                // If JWT token is missing or potentially expired (e.g. after deployment),
                // try to refresh/obtain one in the background without blocking the UI
                authService.ensureJwtToken().then((updatedSession) => {
                    if (updatedSession?.jwtToken) {
                        // Update cookie with refreshed token
                        document.cookie = `jwt-token=${updatedSession.jwtToken}; path=/; max-age=${24 * 60 * 60}; samesite=lax`
                        set({ session: updatedSession })
                        console.log('[AuthStore] JWT token refreshed during initialization')
                    }
                }).catch((error) => {
                    // If refresh fails due to auth error, logout
                    console.error('[AuthStore] JWT token refresh failed:', error)
                    if (error?.status === 401 || error?.status === 403) {
                        console.log('[AuthStore] Auth error during token refresh, logging out')
                        handleAuthError(error)
                    }
                })

                return
            } else {
                // Session expired - clear and redirect to login
                console.log('[AuthStore] Session expired during initialization')
                set({ session: null, user: null, isInitialized: true, isLoading: false })
                handleAuthError({ message: 'Session expired' })
                return
            }
        }

        // Set loading only if we don't have a valid session
        set({ isLoading: true })

        try {
            // Check authService for session
            const session = authService.getSession()

            if (session?.user && session.expiresAt > Date.now()) {
                // If JWT token is missing, try to obtain one
                if (!session.jwtToken) {
                    const updatedSession = await authService.ensureJwtToken()
                    if (updatedSession?.jwtToken) {
                        set({
                            session: updatedSession,
                            user: updatedSession.user,
                            isLoading: false,
                            isInitialized: true,
                            error: null
                        })
                        return
                    }
                }

                set({
                    session,
                    user: session.user,
                    isLoading: false,
                    isInitialized: true,
                    error: null
                })
            } else if (session && session.expiresAt <= Date.now()) {
                // Session expired - clear and redirect to login
                console.log('[AuthStore] Session expired (from authService.getSession)')
                set({
                    isLoading: false,
                    isInitialized: true,
                    session: null,
                    user: null
                })
                handleAuthError({ message: 'Session expired' })
            } else {
                set({
                    isLoading: false,
                    isInitialized: true,
                    session: null,
                    user: null
                })
            }
        } catch (error) {
            console.error('[AuthStore] Error during initialization:', error)
            set({
                isLoading: false,
                isInitialized: true,
                session: null,
                user: null
            })
        }
    },            // Alias for initializeAuth to match interface
    initialize: async function () {
        return this.initializeAuth()
    },

    isAuthenticated: () => {
        const state = get()
        return !!state.session && !!state.user
    },
}))

export const useAuth = () => {
    const store = useAuthStore()
    return {
        user: store.user,
        session: store.session,
        isAuthenticated: !!store.session && !!store.user,
        isLoading: store.isLoading,
        isInitialized: store.isInitialized,
        error: store.error,
        login: store.login,
        loginWithApiKey: store.loginWithApiKey,
        logout: store.logout,
        refreshSession: store.refreshSession,
        extendSession: store.extendSession,
        clearError: store.clearError,
        initializeAuth: store.initializeAuth,
    }
}

export const useUser = () => {
    const user = useAuthStore((state) => state.user)
    return user
}

export const useIsAuthenticated = () => {
    const session = useAuthStore((state) => state.session)
    const user = useAuthStore((state) => state.user)
    return !!session && !!user
}
